import {
   EdgeProps,
   getSmoothStepPath,
   EdgeLabelRenderer,
   BaseEdge,
   useReactFlow,
   useStore,
   Node,
   ReactFlowState,
} from 'reactflow';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { operationType } from '../../services/types';
import { addUpdateNodeToQueue } from '../../services/method';
import { RootState } from '../../redux/store';

function selectNodeFlags(nodeId: string) {
   return (state: ReactFlowState) => {
      const node = state.nodeInternals.get(nodeId);
      return {
         isExecuting: Boolean(node?.data?.isExecuting),
         hasOutput: Boolean(node?.data?.output),
         executionError: Boolean(node?.data?.executionError),
      };
   };
}

export function CustomEdge({
   id,
   source,
   target,
   sourceX,
   sourceY,
   targetX,
   targetY,
   sourcePosition,
   targetPosition,
   data,
}: EdgeProps) {
   const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const sourceFlags = useStore(useCallback(selectNodeFlags(source), [source]));
   const targetFlags = useStore(useCallback(selectNodeFlags(target), [target]));

   const [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
   });

   const lineColor = data?.color || '#8172FF';
   const safeId = id.replace(/[^a-zA-Z0-9_-]/g, '_');
   const glowFilterId = `wf-glow-${safeId}`;

   // Overlays only while a run is in progress — disappear when Run finishes.
   const isError =
      isWorkflowRun &&
      (targetFlags.executionError || (sourceFlags.executionError && !targetFlags.hasOutput));

   const isActive = isWorkflowRun && !isError && sourceFlags.isExecuting;

   // Completed hop during the same run (static, same color) — cleared when run ends.
   const isDoneHop =
      isWorkflowRun &&
      !isError &&
      !isActive &&
      sourceFlags.hasOutput &&
      !sourceFlags.executionError;

   const handleRemove = () => {
      const nodes: Node<operationType>[] = getNodes();
      const edge = getEdges().find((edge) => edge.id === id);
      const targetNode = nodes.find((node) => node.id === edge?.target);
      const sourceNode = nodes.find((node) => node.id === edge?.source);

      setEdges((edges) => edges.filter((edge) => edge.id !== id));

      addUpdateNodeToQueue(setNodes, (nodes) =>
         nodes.map((node) => {
            if (node.id === targetNode?.id && node.data.value?.variables) {
               const variables = { ...node.data.value.variables };
               for (const key in variables.input)
                  variables.input[key] = variables.input[key].filter(
                     (inx: string) => inx !== sourceNode?.data.index
                  );
               return {
                  ...node,
                  data: { ...node.data, value: { ...node.data.value, variables } },
               };
            } else if (node.id === sourceNode?.id && node.data.value?.variables) {
               const variables = { ...node.data.value.variables };
               for (const key in variables.output)
                  variables.output[key] = variables.output[key].filter(
                     (inx: string) => inx !== targetNode?.data.index
                  );
               return {
                  ...node,
                  data: { ...node.data, value: { ...node.data.value, variables } },
               };
            } else return node;
         })
      );
   };

   return (
      <>
         {/* Base edge — subtle dashed stroke when idle */}
         <BaseEdge
            id={id}
            path={edgePath}
            style={{
               stroke: lineColor,
               strokeWidth: 2,
               strokeLinecap: 'round',
               strokeDasharray: '5 7',
               strokeDashoffset: 0,
               opacity: isActive || isDoneHop || isError ? 0.28 : 1,
               animation: isActive || isDoneHop || isError ? undefined : 'wf-edge-dash 2.4s linear infinite',
            }}
         />

         {isActive ? (
            <>
               <defs>
                  <filter
                     id={glowFilterId}
                     filterUnits="userSpaceOnUse"
                     x="-30%"
                     y="-30%"
                     width="160%"
                     height="160%"
                  >
                     <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
                     <feComponentTransfer in="blur" result="softGlow">
                        <feFuncA type="linear" slope="0.55" />
                     </feComponentTransfer>
                     <feMerge>
                        <feMergeNode in="softGlow" />
                        <feMergeNode in="SourceGraphic" />
                     </feMerge>
                  </filter>
               </defs>
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-glow"
                  stroke={lineColor}
                  strokeWidth={10}
                  strokeOpacity={0.22}
                  filter={`url(#${glowFilterId})`}
               />
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-track"
                  stroke={lineColor}
                  strokeWidth={2.5}
                  strokeOpacity={0.35}
               />
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-overlay wf-edge-overlay--running"
                  stroke="#F4F2FF"
                  strokeWidth={2.5}
                  style={{
                     filter: `drop-shadow(0 0 2px ${lineColor}) drop-shadow(0 0 6px ${lineColor}88)`,
                  }}
               />
            </>
         ) : null}

         {isDoneHop ? (
            <>
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-glow"
                  stroke={lineColor}
                  strokeWidth={7}
                  strokeOpacity={0.12}
               />
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-overlay wf-edge-overlay--done"
                  stroke={lineColor}
                  strokeWidth={2.5}
                  strokeOpacity={0.72}
               />
            </>
         ) : null}

         {isError ? (
            <>
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-glow"
                  stroke="#f87171"
                  strokeWidth={9}
                  strokeOpacity={0.18}
               />
               <path
                  d={edgePath}
                  fill="none"
                  className="wf-edge-overlay wf-edge-overlay--error"
                  stroke="#ef4444"
                  strokeWidth={2.5}
               />
            </>
         ) : null}

         <EdgeLabelRenderer>
            <Main
               style={{
                  transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                  pointerEvents: 'all',
               }}
               className="nodrag nopan"
            >
               <button onClick={handleRemove} style={{ opacity: data?.isHover ? 1 : 0 }}>
                  x
               </button>
            </Main>
         </EdgeLabelRenderer>
      </>
   );
}

const Main = styled.div`
   position: absolute;

   button {
      width: 22px;
      height: 22px;
      background-color: #f05454;
      color: #fff;
      border: none;
      cursor: pointer;
      border-radius: 50%;
      font-size: 15px;
      font-weight: 400;
      padding-bottom: 2px;
      box-shadow: 0 0 5px #f05454;
      transition: all 0.2s ease-in-out;

      &:hover {
         opacity: 0.8 !important;
      }
   }
`;
